/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { age } from 'utils/date';
import { useField } from 'formik';
import NumberField from 'shared/components/number-field/NumberField';
import { arrSum, convertKgsToLbs, roundNum } from 'utils/numbers';
import { mapping as genderMapping } from './GenderField';

export default function CaliperMeasurementsField({
  bodyFatPercentageFieldName,
  genderFieldName,
  birthdateYearFieldName,
  weightValueFieldName,
  weightUnitFieldName,
  caliperMethodMapping
}) {
  const chestFieldName = `${bodyFatPercentageFieldName}.chest`;
  const abdominalFieldName = `${bodyFatPercentageFieldName}.abdominal`;
  const thighFieldName = `${bodyFatPercentageFieldName}.thigh`;
  const scubscapularFieldName = `${bodyFatPercentageFieldName}.subscapular`;
  const tricepFieldName = `${bodyFatPercentageFieldName}.tricep`;
  const suprailiacFieldName = `${bodyFatPercentageFieldName}.suprailiac`;
  const midaxillaryFieldName = `${bodyFatPercentageFieldName}.midaxillary`;
  const lowerBackFieldName = `${bodyFatPercentageFieldName}.lower_back`;
  const calfFieldName = `${bodyFatPercentageFieldName}.calf`;
  const bicepFieldName = `${bodyFatPercentageFieldName}.bicep`;

  const [caliperMethodField] = useField(
    `${bodyFatPercentageFieldName}.caliperMethod`
  );
  const [fatPercentageField, , fatPercentageHelpers] = useField(
    `${bodyFatPercentageFieldName}.calculatedFatPercentage`
  );

  const [chestField] = useField(chestFieldName);
  const [abdominalField] = useField(abdominalFieldName);
  const [thighField] = useField(thighFieldName);
  const [scubscapularField] = useField(scubscapularFieldName);
  const [tricepField] = useField(tricepFieldName);
  const [suprailiacField] = useField(suprailiacFieldName);
  const [midaxillaryField] = useField(midaxillaryFieldName);
  const [lowerBackField] = useField(lowerBackFieldName);
  const [calfField] = useField(calfFieldName);
  const [bicepField] = useField(bicepFieldName);

  const [genderField] = useField(genderFieldName);
  const [birthdateYearField] = useField(birthdateYearFieldName);
  const [weightValueField] = useField(weightValueFieldName);
  const [weightUnitField] = useField(weightUnitFieldName);

  var clientAge = 0;

  useEffect(() => {
    clientAge = age(birthdateYearField.value);
    updateFatPercentage();
  }, [
    caliperMethodField.value,
    genderField.value,
    birthdateYearField.value,
    weightValueField.value,
    chestField.value,
    abdominalField.value,
    thighField.value,
    scubscapularField.value,
    tricepField.value,
    suprailiacField.value,
    midaxillaryField.value,
    lowerBackField.value,
    calfField.value,
    bicepField.value
  ]);

  const validateFields = () => {
    return (
      (genderField.value === genderMapping.male.value ||
        genderField.value === genderMapping.female.value) &&
      birthdateYearField.value &&
      weightValueField.value
    );
  };

  const updateFatPercentage = () => {
    var fats = '';
    if (validateFields()) {
      switch (caliperMethodField.value) {
        case caliperMethodMapping.jp3.value:
          fats = jp3([
            chestField.value,
            abdominalField.value,
            thighField.value
          ]);
          break;
        case caliperMethodMapping.jp7.value:
          fats = jp7([
            chestField.value,
            abdominalField.value,
            thighField.value,
            scubscapularField.value,
            tricepField.value,
            suprailiacField.value,
            midaxillaryField.value
          ]);
          break;
        case caliperMethodMapping.parillo9.value:
          fats = parillo9([
            chestField.value,
            abdominalField.value,
            thighField.value,
            scubscapularField.value,
            tricepField.value,
            suprailiacField.value,
            lowerBackField.value,
            calfField.value,
            bicepField.value
          ]);
          break;
        default:
          return;
      }
    }

    fatPercentageHelpers.setValue(
      fats === '' ? '' : fats > 0 ? roundNum(fats) : 0
    );
  };

  const checkExistence = arr => {
    for (let i = 0; i < arr.length; i++) {
      if (!arr[i]) return false;
    }
    return true;
  };

  const jp3 = skinfolds => {
    if (!checkExistence(skinfolds)) return '';

    const skinfoldsSum = arrSum(skinfolds);

    var density = 0;
    switch (genderField.value) {
      case genderMapping.male.value:
        density =
          1.10938 -
          0.0008267 * skinfoldsSum +
          Math.pow(0.0000016 * skinfoldsSum, 2) -
          0.0002574 * clientAge;
        break;
      case genderMapping.female.value:
        density =
          1.0994921 -
          0.0009929 * skinfoldsSum +
          Math.pow(0.0000023 * skinfoldsSum, 2) -
          0.0001392 * clientAge;
        break;
      default:
        return '';
    }

    return 495 / density - 450;
  };

  const jp7 = skinfolds => {
    if (!checkExistence(skinfolds)) return '';

    const skinfoldsSum = arrSum(skinfolds);

    var density = 0;
    switch (genderField.value) {
      case genderMapping.male.value:
        density =
          1.112 -
          0.00043499 * skinfoldsSum +
          Math.pow(0.00000055 * skinfoldsSum, 2) -
          0.00028826 * clientAge;
        break;
      case genderMapping.female.value:
        density =
          1.097 -
          0.00046971 * skinfoldsSum +
          Math.pow(0.00000056 * skinfoldsSum, 2) -
          0.00012828 * clientAge;
        break;
      default:
        return '';
    }

    return 495 / density - 450;
  };

  const parillo9 = skinfolds => {
    if (!checkExistence(skinfolds)) return '';

    const skinfoldsSum = arrSum(skinfolds);

    const weight = Number(
      weightUnitField.value === 'lbs'
        ? weightValueField.value
        : convertKgsToLbs(weightValueField.value)
    );

    return (27 * skinfoldsSum) / weight;
  };

  return (
    <div className="caliper-details">
      <div className="form-inline">
        <MeasurementField
          fieldName={chestFieldName}
          label={caliperMethodMapping.measurements.chest.label}
        />
        <MeasurementField
          fieldName={abdominalFieldName}
          label={caliperMethodMapping.measurements.abdominal.label}
        />
        <MeasurementField
          fieldName={thighFieldName}
          label={caliperMethodMapping.measurements.thigh.label}
        />
      </div>
      {caliperMethodField.value !== caliperMethodMapping.jp3.value && (
        <div className="form-inline">
          <MeasurementField
            fieldName={scubscapularFieldName}
            label={caliperMethodMapping.measurements.subscapular.label}
          />
          <MeasurementField
            fieldName={tricepFieldName}
            label={caliperMethodMapping.measurements.tricep.label}
          />
          <MeasurementField
            fieldName={suprailiacFieldName}
            label={caliperMethodMapping.measurements.suprailiac.label}
          />
        </div>
      )}
      {caliperMethodField.value === caliperMethodMapping.parillo9.value && (
        <div className="form-inline">
          <MeasurementField
            fieldName={lowerBackFieldName}
            label={caliperMethodMapping.measurements.lower_back.label}
          />
          <MeasurementField
            fieldName={calfFieldName}
            label={caliperMethodMapping.measurements.calf.label}
          />
          <MeasurementField
            fieldName={bicepFieldName}
            label={caliperMethodMapping.measurements.bicep.label}
          />
        </div>
      )}

      <div className="form-inline">
        {caliperMethodField.value === caliperMethodMapping.jp7.value && (
          <MeasurementField
            fieldName={midaxillaryFieldName}
            label={caliperMethodMapping.measurements.midaxillary.label}
          />
        )}
        <div className="form-group">
          <label className="form-label">Body Fat %</label>
          <input
            type="text"
            placeholder="0"
            className="form-control"
            {...fatPercentageField}
            disabled
          />
        </div>
      </div>
    </div>
  );
}

function MeasurementField({ fieldName, label, calipersPlaceholder = '0mm' }) {
  return (
    <NumberField
      fieldName={fieldName}
      label={label}
      placeholder={calipersPlaceholder}
    />
  );
}
